export const COUNTRY_CALLING_CODES: string[] = [
    '1',
    '30',
    '31',
    '32',
    '33',
    '36',
    '39',
    '40',
    '41',
    '43',
    '44',
    '45',
    '46',
    '47',
    '48',
    '49',
    '90',
    '359',
    '381',
    '385',
    '386',
    '421',
];

export type PhoneNumberDetails = {
    countryCallingCode: string;
    localNumber: string;
};

// The leading empty entry is the "no country code" option.
export const COUNTRY_CALLING_CODE_OPTIONS: ReadonlyMap<string, string> = new Map([
    ['', ''],
    ...COUNTRY_CALLING_CODES.map((code): [string, string] => [code, `+${code}`]),
]);

const COUNTRY_CALLING_CODES_BY_LENGTH_DESC = [...COUNTRY_CALLING_CODES].sort(
    (a, b) => b.length - a.length,
);

export function getPhoneNumberDetails(phoneNumber: string): PhoneNumberDetails {
    const trimmedPhone = phoneNumber.trim();

    if (trimmedPhone === '') {
        return {
            countryCallingCode: '',
            localNumber: '',
        };
    }

    // Normalization: "00 45 123456" => "+45 123456"
    const normalized = trimmedPhone.replace(/^00\s*/, '+');

    // Case 1: Check if it is an international format with plus
    if (normalized.startsWith('+')) {
        const afterPlus = normalized.slice(1).trimStart();
        const {countryCallingCode, localNumber} =
            extractCountryCallingCodeAndLocalNumber(afterPlus);
        const normalizedLocalNumber = localNumber.startsWith('/')
            ? localNumber.slice(1).trimStart()
            : localNumber;

        return {
            countryCallingCode: countryCallingCode ? countryCallingCode : '',
            localNumber: normalizedLocalNumber,
        };
    }

    // Case 2: Slash-delimited "CC/local" format (e.g., "45/ 123456")
    const slashPosition = trimmedPhone.indexOf('/');
    if (slashPosition !== -1) {
        const possibleCountryCallingCode = normalized.slice(0, slashPosition).trim();
        const isValidCountryCallingCode =
            COUNTRY_CALLING_CODES.indexOf(possibleCountryCallingCode) !== -1;
        const countryCallingCode = isValidCountryCallingCode ? possibleCountryCallingCode : '';

        const localNumber = isValidCountryCallingCode
            ? normalized.slice(slashPosition + 1).trim()
            : normalized;

        return {
            countryCallingCode: countryCallingCode,
            localNumber: localNumber,
        };
    }

    // No recognizable format
    return {
        countryCallingCode: '',
        localNumber: normalized,
    };
}

export function formatPhoneNumber(countryCallingCode: string, localNumber: string): string {
    const trimmedLocalNumber = localNumber.trim();
    if (trimmedLocalNumber === '') {
        return '';
    }

    const trimmedCountryCallingCode = countryCallingCode.trim();
    return trimmedCountryCallingCode
        ? `+${trimmedCountryCallingCode} ${trimmedLocalNumber}`
        : trimmedLocalNumber;
}

function extractCountryCallingCodeAndLocalNumber(phoneNumber: string): PhoneNumberDetails {
    const phone = phoneNumber.trimStart();

    // Check from the longest to shortest to avoid false positives (e.g., +1 = US/Canada, but +1 (242) = the Bahamas)
    const matchedCountryCallingCode = COUNTRY_CALLING_CODES_BY_LENGTH_DESC.find(code =>
        phone.startsWith(code),
    );
    if (matchedCountryCallingCode != null) {
        return {
            countryCallingCode: matchedCountryCallingCode,
            localNumber: phone.slice(matchedCountryCallingCode.length).trimStart(),
        };
    }

    return {
        countryCallingCode: '',
        localNumber: phone,
    };
}
